import React from "react";
import { Card } from "./ui/Card";
import { Stack } from "./ui/Stack";
import { Heading } from "./ui/Heading";
import { Text } from "./ui/Text";
import { Price } from "./ui/Price";
import { Button } from "./ui/Button";
import { getResizedImageUrl, ImagePresets } from "../utils/imageUtils";
import { useStoreLogoPlaceholder } from "../hooks/useStoreLogoPlaceholder";
import { findProductImage } from "../utils/productImageUtils";
import { useTranslation } from "react-i18next";
interface Address {
 firstName: string;
 lastName: string;
 address1: string;
 address2: string;
 city: string;
 province: string;
 postalCode: string;
 country: string;
 phone: string;
}

interface CartItem {
 product: {
  title: string;
  images?: Array<{ url: string; isPrimary?: boolean }>;
  variantOptions?: Array<any>;
  variantCombinations?: Array<any>;
 };
 variant?: {
  name: string;
 };
 selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>;
 quantity: number;
 price: number;
}

interface CheckoutReviewProps {
 shippingAddress: Address | null;
 billingAddress: Address | null;
 useSameAddress: boolean;
 shippingMethod: {
  name: string;
  estimatedDays: { min: number; max: number };
 } | null;
 cartItems: CartItem[];
 subtotal: number;
 tax: number;
 shipping: number;
 total: number;
 currency: string;
 onBack: () => void;
 onConfirm: () => void;
 loading?: boolean;
 isOnlinePaymentEnabled?: boolean;
 notes?: string;
 onNotesChange?: (notes: string) => void;
}

export const CheckoutReview: React.FC<CheckoutReviewProps> = ({
 shippingAddress,
 billingAddress,
 useSameAddress,
 shippingMethod,
 cartItems,
 subtotal,
 tax,
 shipping,
 total,
 currency,
 onBack,
 onConfirm,
 loading = false,
 isOnlinePaymentEnabled = false,
 notes = "",
 onNotesChange,
}) => {
 const { t } = useTranslation();
 const storeLogoPlaceholder = useStoreLogoPlaceholder();
 return (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
   <div className="lg:col-span-2 space-y-6">
    <Card padding="lg">
     <Stack spacing="md">
      <Heading size="lg">{t("checkout.orderSummary")}</Heading>
      <div className="space-y-4">
       {cartItems.map((item, index) => {
        const image = findProductImage(item.product, item.selectedVariantOptions);
        const imageUrl = image ? getResizedImageUrl(image.url, ImagePresets.checkoutReview, storeLogoPlaceholder) : storeLogoPlaceholder;
        return (
         <div key={index} className="flex gap-4">
          {image && (
           <img
            src={imageUrl}
            alt={item.product.title}
            className="w-20 h-20 object-cover rounded"
           />
          )}
          <div className="flex-1">
           <Text weight="semibold">{item.product.title}</Text>
           {item.variant && (
            <Text size="sm" className="text-gray-500">
             {t("checkout.variantLabel")}{item.variant.name}
            </Text>
           )}
           {item.selectedVariantOptions && item.selectedVariantOptions.length > 0 && (
            <div className="space-y-0.5">
             {item.selectedVariantOptions.map((option, idx) => (
              <Text key={idx} size="sm" className="text-gray-500">
               {option.optionName}: {option.optionValue}
              </Text>
             ))}
            </div>
           )}
           <Text size="sm" className="text-gray-500">
            {t("checkout.quantityLabel")}{item.quantity}
           </Text>
           <Price amount={item.price * item.quantity} currency={currency} />
          </div>
         </div>
        );
       })}
      </div>
     </Stack>
    </Card>

    {shippingAddress && (
     <Card padding="lg">
      <Stack spacing="md">
       <Heading size="lg">{t("checkout.shippingAddress")}</Heading>
       <div className="text-sm">
        <Text>
         {shippingAddress.firstName} {shippingAddress.lastName}
        </Text>
        <Text>{shippingAddress.address1}</Text>
        {shippingAddress.address2 && <Text>{shippingAddress.address2}</Text>}
        <Text>
         {shippingAddress.city}, {shippingAddress.province} {shippingAddress.postalCode}
        </Text>
        <Text>{shippingAddress.country}</Text>
        {shippingAddress.phone && <Text>{t("checkout.phoneLabel")}{shippingAddress.phone}</Text>}
       </div>
      </Stack>
     </Card>
    )}

    {!useSameAddress && billingAddress ? (
     <Card padding="lg">
      <Stack spacing="md">
       <Heading size="lg">{t("checkout.billingAddress")}</Heading>
       <div className="text-sm">
        <Text>
         {billingAddress.firstName} {billingAddress.lastName}
        </Text>
        <Text>{billingAddress.address1}</Text>
        {billingAddress.address2 && <Text>{billingAddress.address2}</Text>}
        <Text>
         {billingAddress.city}, {billingAddress.province} {billingAddress.postalCode}
        </Text>
        <Text>{billingAddress.country}</Text>
        {billingAddress.phone && <Text>{t("checkout.phoneLabel")}{billingAddress.phone}</Text>}
       </div>
      </Stack>
     </Card>
    ) : null}

    {shippingMethod && (
     <Card padding="lg">
      <Stack spacing="md">
       <Heading size="lg">{t("checkout.shippingMethod")}</Heading>
       <Text>
        {shippingMethod.name} ({shippingMethod.estimatedDays.min}-{shippingMethod.estimatedDays.max} {t("checkout.days")})
       </Text>
      </Stack>
     </Card>
    )}

    <Card padding="lg">
     <Stack spacing="md">
      <Heading size="lg">{t("checkout.orderNotes")} ({t("checkout.optional")})</Heading>
      <Text size="sm" className="text-gray-600 mb-2">
       {t("checkout.orderNotesPlaceholder")}
      </Text>
      <textarea
       value={notes}
       onChange={(e) => onNotesChange?.(e.target.value)}
       placeholder={t("checkout.orderNotesHint")}
       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-store-primary focus:border-store-primary resize-none"
       rows={4}
       maxLength={1000}
      />
      <Text size="xs" className="text-gray-500">
       {notes.length}/1000 {t("checkout.characters")}
      </Text>
     </Stack>
    </Card>
   </div>

   <div>
    <Card padding="lg">
     <Stack spacing="md">
      <Heading size="lg">{t("checkout.orderTotal")}</Heading>
      <div className="space-y-2">
       <div className="flex justify-between">
        <Text>{t("checkout.subtotal")}</Text>
        <Price amount={subtotal} currency={currency} />
       </div>
       <div className="flex justify-between">
        <Text>{t("checkout.tax")}</Text>
        <Price amount={tax} currency={currency} />
       </div>
       <div className="flex justify-between">
        <Text>{t("checkout.shipping")}</Text>
        <Price amount={shipping} currency={currency} />
       </div>
       <hr />
       <div className="flex justify-between">
        <Text weight="semibold" size="lg">
         {t("checkout.total")}
        </Text>
        <Price amount={total} currency={currency} size="lg" />
       </div>
      </div>

      <Stack spacing="sm" className="mt-6">
       <Button 
        onClick={onConfirm} 
        loading={loading} 
        disabled={loading}
        className="w-full"
       >
        {isOnlinePaymentEnabled ? t("checkout.continueToPayment") : t("checkout.placeOrder")}
       </Button>
       <Button variant="outline" onClick={onBack} className="w-full">
        {t("checkout.back")}
       </Button>
      </Stack>
     </Stack>
    </Card>
   </div>
  </div>
 );
};

